import { useEffect } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { useCurrentUser, usePermissions } from '@klareospace/pharos-lib-front-utils';
import {
  SETTINGS_ACCESS_PERMISSIONS,
  SITE_SETTINGS_PERMISSIONS,
  SPACE_DETAIL_ACCESS_PERMISSIONS,
  resolveSettingsNavigation,
} from '&/features/settings/shared/settings-navigation';
import { useUserScopes } from '&/hooks/use-user-scopes';
import { useMasterContext } from '&/master';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface UseSettingsScopeRedirectOptions {
  /**
   * When true (SettingsLayout), the resolver checks `isReady` and `canAccessSettings`
   * before other branches, mirroring the cold-load-race fallback in the layout component.
   * When false (SettingsIndexRedirect), those checks are skipped (beforeLoad guards them).
   */
  includeAccessFallback: boolean;
}

// ─── Hook ─────────────────────────────────────────────────────────────────────

/**
 * Shared settings-area redirect hook. Consumes MasterContext, permissions, and scope facts
 * and navigates to the correct settings destination via `resolveSettingsNavigation`.
 *
 * One `useEffect` performs navigation; while `status === 'loading'` or when the target
 * is null (stay put), no navigation occurs.
 *
 * Consumed by:
 * - `SettingsLayout` with `{ includeAccessFallback: true }`.
 * - `SettingsIndexRedirect` with `{ includeAccessFallback: false }`.
 */
export function useSettingsScopeRedirect({ includeAccessFallback }: UseSettingsScopeRedirectOptions): void {
  const { hasAnyPermission, isReady } = usePermissions();
  const { siteId, spaceId } = useMasterContext();
  const { user } = useCurrentUser();
  const { firstSiteId, isLoading: isScopeLoading, isSiteOnlyScoped } = useUserScopes(user);
  const navigate = useNavigate();

  const canAccessSettings = hasAnyPermission(SETTINGS_ACCESS_PERMISSIONS);
  const canEnterSiteSettings = hasAnyPermission(SITE_SETTINGS_PERMISSIONS);
  const canEnterSpaceDetail = hasAnyPermission(SPACE_DETAIL_ACCESS_PERMISSIONS);

  useEffect((): void => {
    const resolution = resolveSettingsNavigation({
      canAccessSettings,
      canEnterSiteSettings,
      canEnterSpaceDetail,
      firstSiteId,
      includeAccessFallback,
      isReady,
      isScopeLoading,
      isSiteOnlyScoped,
      siteId,
      spaceId,
    });

    if (resolution.status === 'loading') return;
    const { target } = resolution;
    if (target === null) return;

    if (target.route === 'home') {
      void navigate({ to: '/' });
      return;
    }
    if (target.route === 'site') {
      void navigate({ params: { siteId: target.siteId }, to: '/settings/sites/$siteId' });
      return;
    }
    void navigate({ params: { spaceId: target.spaceId }, to: '/settings/spaces/$spaceId' });
  }, [
    canAccessSettings,
    canEnterSiteSettings,
    canEnterSpaceDetail,
    firstSiteId,
    includeAccessFallback,
    isReady,
    isScopeLoading,
    isSiteOnlyScoped,
    navigate,
    siteId,
    spaceId,
  ]);
}
